import { ThunkDispatch } from 'redux-thunk';
import token, { JWTToken } from 'auth/token';
import moment from 'moment';
import { setAuthToken } from 'apiClient';
import authApi from 'auth/api';
import User from 'auth/User';
import { AuthStoreState } from 'auth/store/reducer';
import publicEvents from 'utils/publicEvents/publicEvents';
import userLoggedInEvent from 'utils/publicEvents/events/userLoggedInEvent';
import userLoggedOutEvent from 'utils/publicEvents/events/userLoggedOutEvent';
import { removeFeatureToggles, setFeatureToggles } from 'utils/featureToggle';
import { Nullable } from 'utils/types';

type Dispatch = ThunkDispatch<AuthStoreState, null, any>;

export enum ACTIONS {
  SET_USER_DATA = 'AUTH/SET_USER_DATA',
  CLEAR_USER = 'AUTH/CLEAR_USER',
  SET_ACCESS_TOKEN = 'AUTH/SET_ACCESS_TOKEN',
  SET_LOGIN_ERROR = 'AUTH/SET_LOGIN_ERROR',
  SET_IS_LOGGING = 'AUTH/SET_IS_LOGGING',
}

export type Action = {
  type: ACTIONS;
  payload?: any;
};

const setUser = (user: User): Action => ({
  type: ACTIONS.SET_USER_DATA,
  payload: {
    user,
  },
});

const setLoginError = (error: any): Action => ({
  type: ACTIONS.SET_LOGIN_ERROR,
  payload: {
    loginError: error,
  },
});

const setIsLogging = (isLogging: boolean): Action => ({
  type: ACTIONS.SET_IS_LOGGING,
  payload: {
    isLogging,
  },
});

const clearLoginError = (): Action => ({
  type: ACTIONS.SET_LOGIN_ERROR,
  payload: {
    loginError: null,
  },
});

const clearUser = (): Action => ({
  type: ACTIONS.CLEAR_USER,
  payload: {},
});

const setAccessToken = (accessToken: Nullable<string>): Action => {
  setAuthToken(accessToken);
  return {
    type: ACTIONS.SET_ACCESS_TOKEN,
    payload: accessToken ? token.create(accessToken) : null,
  };
};

let reloadTimer: any = null;

const setReloadAccessTokenTimer = (jwt: JWTToken, dispatch: Dispatch) => {
  if (!jwt.expirationTimeStamp) {
    throw new Error('No expiration in access token');
  }

  const expiration = moment.unix(jwt.expirationTimeStamp);
  if (reloadTimer) {
    clearTimeout(reloadTimer);
  }
  const reload = async () => {
    const secondsToExpire = expiration.diff(moment.now(), 'seconds');
    if (secondsToExpire <= 60) {
      try {
        await dispatch(reloadAccessToken());
      } catch (e) {
        await dispatch(logout());
      }
    } else {
      reloadTimer = setTimeout(reload, 500);
    }
  };
  reload();
};

export const logout = () => {
  return async (dispatch: Dispatch) => {
    try {
      await authApi.logout();
    } catch (e) {}
    if (reloadTimer) {
      clearTimeout(reloadTimer);
    }
    dispatch(setAccessToken(null));
    dispatch(clearUser());
    removeFeatureToggles();
    publicEvents.dispatch(userLoggedOutEvent.create());
  };
};

export const login = (username: string, password: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(clearUser());
      dispatch(setIsLogging(true));
      const data = await authApi.login(username, password);
      const jwt = token.create(data.access);
      const user = new User(jwt.userId, data.name, data.role, data.permissions, username, data.advisers);
      dispatch(setAccessToken(jwt.access));
      dispatch(setUser(user));
      setReloadAccessTokenTimer(jwt, dispatch);
      setFeatureToggles(data.feature_toggles);

      publicEvents.dispatch(userLoggedInEvent.create(user.id, user.name, user.email));
      dispatch(clearLoginError());
    } catch (e) {
      dispatch(setLoginError(e.response ? e.response : e));
    }
    dispatch(setIsLogging(false));
  };
};

export const reloadAccessToken = () => {
  return async (dispatch: Dispatch) => {
    const accessToken = await authApi.getAccessToken();
    dispatch(setAccessToken(accessToken));
    setReloadAccessTokenTimer(token.create(accessToken), dispatch);
  };
};

export const loadUserData = () => {
  return async (dispatch: Dispatch) => {
    try {
      const userData = await authApi.getUserData();
      const user = new User(
        userData.id,
        userData.name,
        userData.role,
        userData.permissions,
        userData.email,
        userData.advisers,
      );
      dispatch(setUser(user));
      setFeatureToggles(userData.feature_toggles);
      publicEvents.dispatch(userLoggedInEvent.create(user.id, user.name, user.email));
    } catch (e) {}
  };
};

export const initAuth = () => {
  return async (dispatch: Dispatch) => {
    try {
      await dispatch(reloadAccessToken());
      await dispatch(loadUserData());
    } catch (e) {}
  };
};
